import { FiActivity, FiAlertCircle, FiBookOpen, FiBriefcase, FiCreditCard, FiDollarSign, FiDownload, FiFileText, FiGlobe, FiGrid, FiHome, FiKey, FiLayers, FiPackage, FiSettings, FiShield, FiTag, FiTerminal, FiTruck, FiUserCheck, FiUsers } from 'react-icons/fi';
import useAuthStore from "store/authStore";
import { Permission } from "utils/server-constants/permissions";
import { Role } from "utils/server-constants/roles";

const useMenuItems = () => {
	const { hasRoles, hasPermissions } = useAuthStore();
	const ICON_SIZE = 18;
	let adminDashboard = "/admin-dashboard";

	if (hasRoles([Role.SeniorPublisher, Role.Publisher])) {
		adminDashboard = "/publisher-dashboard";
	} else if (hasRoles([Role.Institution, Role.Educator])) {
		adminDashboard = "/institution-dashboard";
	} else if (hasRoles([Role.Ba])) {
		adminDashboard = "/ba-dashboard";
	}

	const menu = [
		{
			type: "group",
			id: "overview",
			label: "Overview",
			icon: <FiHome size={ICON_SIZE} />,
			children: [
				{
					type: "link",
					to: adminDashboard,
					label: "Dashboard",
					icon: <FiHome size={ICON_SIZE} />,
					show: hasRoles([
						Role.SuperAdmin,
						Role.Admin,
						Role.SeniorPublisher,
						Role.Publisher,
						Role.Institution,
						Role.Educator,
						Role.Ba,
					]),
				},
				{
					type: "link",
					to: "/ba/client-payments",
					label: "Statement",
					icon: <FiFileText size={ICON_SIZE} />,
					show: hasRoles([Role.Ba]),
				},
			],
		},
		{
			type: "group",
			id: "books",
			label: "Library",
			icon: <FiBookOpen size={ICON_SIZE} />,
			children: [
				{
					type: "link",
					to: "/books/admin/all",
					label: "Books",
					icon: <FiBookOpen size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin, Role.SeniorPublisher, Role.Publisher]) &&
						hasPermissions([Permission.ViewBooks]),
				},
				{
					type: "link",
					to: "/admin/physical-books",
					label: "Physical books",
					icon: <FiPackage size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewBooks]),
				},
				{
					type: "link",
					to: "/books/institution",
					label: "Purchased books",
					icon: <FiBookOpen size={ICON_SIZE} />,
					show:
						hasRoles([Role.Institution, Role.Educator]) && hasPermissions([Permission.ViewBooks]),
				},
				{
					type: "link",
					to: "/books/institution-picks",
					label: "Subscription picks",
					icon: <FiBookOpen size={ICON_SIZE} />,
					show:
						hasRoles([Role.Institution, Role.Educator]) && hasPermissions([Permission.ViewBooks]),
				},
				{
					type: "link",
					to: "/books/admin/genres-and-categories",
					label: "Book categories",
					icon: <FiGrid size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin]) &&
						hasPermissions([Permission.ViewCategory, Permission.ViewGenre]),
				},
				{
					type: "link",
					to: "/books/admin/tags",
					label: "Book tags",
					icon: <FiTag size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin, Role.Admin]),
				},
				{
					type: "link",
					to: "/admin/geo-restrictions",
					label: "Geographic restrictions",
					icon: <FiGlobe size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin, Role.Admin, Role.SeniorPublisher, Role.Publisher]),
				},
			],
		},
		{
			type: "group",
			id: "users-and-orgs",
			label: "People & organisations",
			icon: <FiUsers size={ICON_SIZE} />,
			children: [
				{
					type: "link",
					to: "/users/admin/all",
					label: "Users",
					icon: <FiUsers size={ICON_SIZE} />,
					show:
						hasRoles([
							Role.SuperAdmin,
							Role.Admin,
							Role.SeniorPublisher,
							Role.Publisher,
							Role.Institution,
							Role.Educator,
						]) && hasPermissions([Permission.ViewUser]),
				},
				{
					type: "link",
					to: "/institutions",
					label: "Institutions",
					icon: <FiBriefcase size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewInstitution]),
				},
				{
					type: "link",
					to: "/publishers",
					label: "Publishers",
					icon: <FiBriefcase size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewPublisher]),
				},
				{
					type: "link",
					to: "/subject-groups",
					label: "Subject groups",
					icon: <FiLayers size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin, Role.Institution, Role.Educator]) &&
						hasPermissions([Permission.ViewSubjectGroup]),
				},
				{
					type: "link",
					to: "/brand-ambassadors",
					label: "Brand ambassadors",
					icon: <FiUserCheck size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewUser]),
				},
			],
		},
		{
			type: "group",
			id: "commerce",
			label: "Sales & payments",
			icon: <FiTag size={ICON_SIZE} />,
			children: [
				{
					type: "link",
					to: "/orders",
					label: "Orders",
					icon: <FiPackage size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewOrder]),
				},
				{
					type: "link",
					to: "/orders-to-assign",
					label: "Assign orders",
					icon: <FiTruck size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin]) &&
						hasPermissions([Permission.ViewOrder, Permission.AdminCourier]),
				},
				{
					type: "link",
					to: "/admin/subscription-plans",
					label: "Subscription plans",
					icon: <FiCreditCard size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewVoucher]),
				},
				{
					type: "link",
					to: "/vouchers",
					label: "Vouchers",
					icon: <FiTag size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewVoucher]),
				},
				{
					type: "link",
					to: "/books/publisher/purchased",
					label: "Sold books",
					icon: <FiBookOpen size={ICON_SIZE} />,
					show:
						hasRoles([Role.SeniorPublisher, Role.Publisher]) &&
						hasPermissions([Permission.ViewSoldBooks]),
				},
				{
					type: "link",
					to: "/admin/publishers/sold-books-export",
					label: "Publisher sales export",
					icon: <FiDownload size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewSoldBooks]),
				},
				{
					type: "link",
					to: "/pay-publisher",
					label: "Publisher payouts",
					icon: <FiDollarSign size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin]) &&
						hasPermissions([Permission.RecordPubPayouts]),
				},
				{
					type: "link",
					to: "/pay-brand-ambassador",
					label: "Ambassador payouts",
					icon: <FiDollarSign size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.RecordBaPayouts]),
				},
			],
		},
		{
			type: "group",
			id: "system",
			label: "Workspace",
			icon: <FiSettings size={ICON_SIZE} />,
			children: [
				{
					type: "link",
					to: "/configurations",
					label: "Configuration",
					icon: <FiSettings size={ICON_SIZE} />,
					show: hasPermissions([Permission.ViewConfiguration]),
				},
				{
					type: "link",
					to: "/admin/currencies",
                    label: "Currencies",
                    icon: <FiDollarSign size={ICON_SIZE} />,
                    show: hasRoles([Role.SuperAdmin, Role.Admin]),
                },
				{
                    type: "link",
                    to: "/admin/payment-methods",
					label: "Payment methods",
					icon: <FiCreditCard size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin, Role.Admin]),
				},
				{
					type: "link",
					to: "/admin/service-tokens",
					label: "Service tokens",
					icon: <FiKey size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin]),
				},
				{
					type: "link",
					to: "/roles-and-permissions",
					label: "Roles & permissions",
					icon: <FiShield size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewRole]),
				},
				{
					type: "link",
					to: "/audit-trail",
					label: "Audit trail",
					icon: <FiActivity size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin]),
				},
				{
					type: "link",
					to: "/error-report",
					label: "Error report",
					icon: <FiAlertCircle size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin]) && hasPermissions([Permission.ViewErrors]),
				},
				{
					type: "link",
					to: "/command-center",
					label: "Command center",
					icon: <FiTerminal size={ICON_SIZE} />,
					show: (hasRoles([Role.SuperAdmin]) && hasPermissions([Permission.RunCli, Permission.PublishConfigurations])) || hasPermissions([Permission.ReconcileEcocash]),
				},
			],
		},
	];

	return menu
		.map((item) => {
			if (item.type !== "group") {
				if (!item.show) {
					return null;
				}

				const { show, ...visibleItem } = item;
				return visibleItem;
			}

			const visibleChildren = item.children
				.filter((child) => child.show)
				.map(({ show, ...child }) => child);

			if (visibleChildren.length === 0) {
				return null;
			}

			return {
				...item,
				children: visibleChildren,
			};
		})
		.filter(Boolean);
};

export default useMenuItems;
